// Everything the trip map and its panels show, worked out once from the trip detail the server
// sends (customer link, owner portal or admin). No drawing here - see TripMap.jsx.

// Day 1 violet, Day 2 blue, Day 3 orange, then teal, pink, olive; longer trips go round again.
export const DAY_COLORS = ["#6d28d9", "#2563eb", "#c2410c", "#0f766e", "#be185d", "#4d7c0f"];
export const HOTEL_COLOR = "#0f2f4a";
export const PICKUP_COLOR = "#047857";
// The same circles the server marks the trip by (TripVisitDetector): inside the arrival circle for
// 5 minutes is Reached, beyond the leave ring is Left. The leave ring is the office's view only.
// The server sends the values set on Tracking Settings, and a place may have its own circle; these
// are only for an older server that sends none.
export const ARRIVAL_RADIUS_M = 500;
export const LEAVE_RADIUS_M = 1000;
const DWELL_MINUTES = 5;
// The driver app sends a point every minute while moving and every ten when still, so twenty
// minutes of silence means the phone has stopped sending, not that the vehicle is parked.
export const VEHICLE_STALE_MINUTES = 20;
// A point this rough would draw the trail through fields; the office can still see it as a raw point.
const TRAIL_MAX_ACCURACY_M = 150;

export const dayColor = (index) => DAY_COLORS[((index % DAY_COLORS.length) + DAY_COLORS.length) % DAY_COLORS.length];

export function buildTripMapModel(detail, now = new Date()) {
  if (!detail) return null;
  const lastMoveKey = latestPlaceMove(detail.places || []);
  const tripEnded = Boolean(detail.tripEnded);
  const arrivalRadiusM = toNumber(detail.arrivalRadiusM) ?? ARRIVAL_RADIUS_M;
  const leaveRadiusM = toNumber(detail.leaveRadiusM) ?? LEAVE_RADIUS_M;

  const days = [];
  const dayByKey = new Map();
  (detail.places || []).forEach((place) => {
    const key = place.dayKey || "day_0";
    if (!dayByKey.has(key)) {
      const index = days.length;
      const day = {
        key,
        index,
        label: place.dayLabel || `Day ${index + 1}`,
        title: place.dayTitle || "",
        color: dayColor(index),
        places: [],
      };
      days.push(day);
      dayByKey.set(key, day);
    }
    dayByKey.get(key).places.push(place);
  });

  const currentDayIndex = currentDay(detail, days.length, now);
  days.forEach((day) => {
    let number = 0;
    // A day that is over: its places never reached are "missed" - the office sees "Not visited".
    const dayOver = tripEnded || day.index < currentDayIndex;
    day.places = day.places.map((place) => {
      const lat = toNumber(place.latitude);
      const lng = toNumber(place.longitude);
      const status = placeStatus(place, lastMoveKey, tripEnded);
      return {
        key: place.key,
        label: place.label || "-",
        number: ++number,
        lat,
        lng,
        hasPoint: lat != null && lng != null,
        status: status === "upcoming" && dayOver ? "missed" : status,
        // Marked from the route by the server rather than tapped by the driver.
        auto: isAutoMark(place.dropped) || isAutoMark(place.pickedUp),
        dropped: place.dropped || null,
        pickedUp: place.pickedUp || null,
        mapLink: place.googleMapLink || null,
        arrivalRadiusM: toNumber(place.arrivalRadiusM) ?? arrivalRadiusM,
        leaveRadiusM: toNumber(place.leaveRadiusM) ?? leaveRadiusM,
        dayIndex: day.index,
        color: day.color,
      };
    });
    day.visitedCount = day.places.filter((stop) => stop.status === "visited" || stop.status === "here").length;
    day.missedCount = day.places.filter((stop) => stop.status === "missed").length;
  });

  const hotels = groupHotels(detail.dayHotels || []);
  days.forEach((day) => {
    // Night N is the night of Day N: tonight's stay for Day N, and where Day N + 1 starts.
    day.tonight = hotels.filter((hotel) => hotel.nights.includes(day.index + 1));
    day.startStay = day.index === 0 ? [] : hotels.filter((hotel) => hotel.nights.includes(day.index));
  });

  const allPlaces = days.flatMap((day) => day.places);
  const here = allPlaces.find((stop) => stop.status === "here") || null;
  const lastVisited = [...allPlaces]
    .filter((stop) => stop.status === "visited")
    .sort((a, b) => eventTime(b.pickedUp || b.dropped) - eventTime(a.pickedUp || a.dropped))[0] || null;
  const next = tripEnded ? null : nextUpcoming(days, here || lastVisited, currentDayIndex);

  // The stays, from the visits the server works out from the trail (an older server sends none).
  const stayVisits = (detail.stayVisits || []).filter((visit) => toNumber(visit.night) != null && visit.arrivedIst);
  const stayOf = (visit) => hotels.find((hotel) => samePoint(hotel, visit))
    || hotels.find((hotel) => hotel.nights.includes(Number(visit.night))) || null;
  const lastStayVisit = stayVisits[stayVisits.length - 1] || null;
  days.forEach((day) => {
    // Per stay row: first reached that night, and then either back again now or last left.
    day.stayVisit = {};
    day.tonight.forEach((hotel) => {
      const visits = stayVisits.filter((visit) => Number(visit.night) === day.index + 1 && stayOf(visit) === hotel);
      if (!visits.length) return;
      const last = visits[visits.length - 1];
      const hereNow = !tripEnded && !last.leftIst && last === lastStayVisit;
      day.stayVisit[hotel.key] = {
        arrivedIst: visits[0].arrivedIst,
        backIst: hereNow && visits.length > 1 ? last.arrivedIst : null,
        leftIst: last.leftIst || null,
        here: hereNow,
      };
    });
  });
  const openStay = !tripEnded && lastStayVisit && !lastStayVisit.leftIst ? lastStayVisit : null;
  // A place reached after the vehicle got to the stay means it has moved on from there.
  const atStay = openStay && !(here && eventTime(here.dropped) > parseTime(openStay.arrivedIst))
    ? {
        hotel: stayOf(openStay),
        arrivedIst: openStay.arrivedIst,
        back: stayVisits.some((visit) => visit !== openStay
          && Number(visit.night) === Number(openStay.night) && stayOf(visit) === stayOf(openStay)),
        // The morning after, the new day's first place; a place left over from that evening is not next.
        next: next && next.dayIndex >= Number(openStay.night) ? next : null,
      }
    : null;
  // Out again after reaching tonight's stay, with none of today's places left to go to.
  const lastTonight = [...stayVisits].reverse().find((visit) => Number(visit.night) === currentDayIndex + 1) || null;
  const leftStay = !tripEnded && !atStay && !here && !next && lastTonight?.leftIst
    && parseTime(lastTonight.leftIst) >= (lastVisited ? eventTime(lastVisited.pickedUp || lastVisited.dropped) : 0)
    ? { hotel: stayOf(lastTonight), leftIst: lastTonight.leftIst }
    : null;

  const locations = (detail.driverLocations || [])
    .map((item) => ({
      ...item,
      lat: toNumber(item.latitude),
      lng: toNumber(item.longitude),
      at: parseTime(item.capturedAtIst),
    }))
    .filter((item) => item.lat != null && item.lng != null);
  const latest = locations[locations.length - 1] || null;
  const vehicle = latest
    ? {
        lat: latest.lat,
        lng: latest.lng,
        heading: toNumber(latest.heading),
        capturedAtIst: latest.capturedAtIst,
        minutesAgo: latest.at ? Math.max(0, Math.round((now.getTime() - latest.at) / 60000)) : null,
        stale: !latest.at || now.getTime() - latest.at > VEHICLE_STALE_MINUTES * 60000 || tripEnded,
      }
    : null;

  const trail = days.length ? days.map(() => []) : [[]];
  locations.forEach((point) => {
    if (point.accuracy != null && Number(point.accuracy) > TRAIL_MAX_ACCURACY_M) return;
    const index = trailDayIndex(point, dayByKey, detail, days.length);
    trail[index].push({ lat: point.lat, lng: point.lng, at: point.at });
  });

  const pinned = allPlaces.filter((stop) => stop.hasPoint);
  const pinnedHotels = hotels.filter((hotel) => hotel.hasPoint);
  const pickup = pickupStop(detail);
  return {
    days,
    hotels,
    pickup,
    trail,
    rawPoints: locations,
    vehicle,
    currentDayIndex,
    here,
    next,
    lastVisited,
    // The vehicle at a stay right now ({ hotel, arrivedIst, back, next }), or out again from
    // tonight's stay ({ hotel, leftIst }); null otherwise.
    atStay,
    leftStay,
    tripStarted: Boolean(detail.tripStarted),
    tripEnded,
    hasMapContent: pinned.length > 0 || pinnedHotels.length > 0 || Boolean(pickup) || Boolean(vehicle),
    // For the stays, the pickup and the legend; a place with its own circle carries it.
    arrivalRadiusM,
    leaveRadiusM,
    dwellMinutes: toNumber(detail.dwellMinutes) ?? DWELL_MINUTES,
  };
}

/** 500 -> "500 m", 1000 -> "1 km", 1500 -> "1.5 km". */
export function distanceLabel(meters) {
  return meters >= 1000 ? `${Number((meters / 1000).toFixed(1))} km` : `${meters} m`;
}

export function isAutoMark(event) {
  return event?.gpsProofType === "AUTO";
}

// Direction arrows on the driven route: one every this many screen pixels, so they stay evenly
// spaced at any zoom. A line shorter than the minimum gets none.
export const ROUTE_ARROW_SPACING_PX = 90;
const ROUTE_ARROW_MIN_LINE_PX = 24;
// The arrow points along the route this far either side of it, so one jittery GPS point cannot
// turn it sideways.
const ROUTE_ARROW_AIM_PX = 8;

/**
 * Where the arrows go on one line, given its points in screen pixels ({x, y}, in driving order):
 * the first half a spacing in, then one every spacing; a line shorter than one spacing gets one in
 * its middle. Each comes back with the angle it points, in degrees clockwise from east.
 */
export function routeArrows(points, spacing = ROUTE_ARROW_SPACING_PX) {
  const segments = [];
  let total = 0;
  for (let index = 1; index < (points || []).length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    const length = Math.hypot(to.x - from.x, to.y - from.y);
    if (length > 0) {
      segments.push({ from, to, length, start: total });
      total += length;
    }
  }
  if (total < ROUTE_ARROW_MIN_LINE_PX) return [];

  const pointAt = (distance) => {
    const along = Math.min(Math.max(distance, 0), total);
    const segment = segments.find((item) => along <= item.start + item.length) || segments[segments.length - 1];
    const share = (along - segment.start) / segment.length;
    return {
      x: segment.from.x + (segment.to.x - segment.from.x) * share,
      y: segment.from.y + (segment.to.y - segment.from.y) * share,
    };
  };
  const distances = [];
  if (total < spacing) {
    distances.push(total / 2);
  } else {
    for (let distance = spacing / 2; distance < total; distance += spacing) distances.push(distance);
  }
  return distances.map((distance) => {
    const behind = pointAt(distance - ROUTE_ARROW_AIM_PX);
    const ahead = pointAt(distance + ROUTE_ARROW_AIM_PX);
    return { ...pointAt(distance), angle: (Math.atan2(ahead.y - behind.y, ahead.x - behind.x) * 180) / Math.PI };
  });
}

/** The customer may set or change the pickup until the driver reaches it or the trip starts. */
export function canChangePickup(detail) {
  if (!detail || detail.driverReachedPickup || detail.tripStarted || detail.tripEnded) return false;
  return detail.tripStatus !== "COMPLETED" && detail.tripStatus !== "CANCELLED";
}

// The customer's own pickup spot once they set one; before that, the trip's general pickup point.
function pickupStop(detail) {
  const lat = toNumber(detail.pickupLatitude);
  const lng = toNumber(detail.pickupLongitude);
  if (lat == null || lng == null) return null;
  const point = detail.pickupPoint || null;
  return {
    lat,
    lng,
    exact: Boolean(point),
    landmark: point?.landmark || "",
    contactPhone: point?.contactPhone || "",
    mapLink: detail.pickupGoogleMapLink || null,
    time: detail.vehiclePickupDateTime || null,
    reached: detail.driverReachedPickup || null,
  };
}

// A place still marked dropped but never picked up only counts as "here" while it is the latest
// thing the driver marked; once the vehicle has moved on to mark something else, it was visited.
function placeStatus(place, lastMoveKey, tripEnded) {
  if (place.pickedUp) return "visited";
  if (place.dropped) return !tripEnded && place.key === lastMoveKey ? "here" : "visited";
  return "upcoming";
}

function latestPlaceMove(places) {
  let latest = null;
  places.forEach((place) => {
    if (place.dropped && (!latest || eventTime(place.dropped) >= latest.at)) {
      latest = { key: place.key, at: eventTime(place.dropped), left: false };
    }
    if (place.pickedUp && (!latest || eventTime(place.pickedUp) >= latest.at)) {
      latest = { key: place.key, at: eventTime(place.pickedUp), left: true };
    }
  });
  return latest && !latest.left ? latest.key : null;
}

// Never looks past today: once today's places are done the vehicle is heading to the night's stay,
// not to tomorrow's first place. A day running late still leads to its own remaining places.
function nextUpcoming(days, from, currentDayIndex) {
  const startDay = from ? from.dayIndex : Math.max(currentDayIndex, 0);
  const lastDay = Math.min(Math.max(currentDayIndex, startDay), days.length - 1);
  for (let index = startDay; index <= lastDay; index += 1) {
    const candidates = days[index].places.filter((stop) => stop.status === "upcoming");
    const after = from && index === from.dayIndex
      ? candidates.filter((stop) => stop.number > from.number)
      : candidates;
    if (after.length) return after[0];
  }
  return null;
}

// The same stay over several nights is one pin, labelled with all its nights.
function groupHotels(dayHotels) {
  const groups = new Map();
  dayHotels.forEach((hotel) => {
    const lat = toNumber(hotel.latitude);
    const lng = toNumber(hotel.longitude);
    const id = hotel.hotelCode || hotel.hotelName || hotel.hotelId || `${lat},${lng}`;
    const key = `${id}|${lat == null ? "" : lat.toFixed(4)}|${lng == null ? "" : lng.toFixed(4)}`;
    if (!groups.has(key)) {
      groups.set(key, {
        key,
        name: hotel.hotelName || "Stay",
        lat,
        lng,
        hasPoint: lat != null && lng != null,
        mapLink: hotel.googleMapLink || null,
        caretakerName: hotel.caretakerName || "",
        caretakerNumber: hotel.caretakerNumber || "",
        nights: [],
      });
    }
    const group = groups.get(key);
    const night = Number(hotel.dayNumber);
    if (Number.isFinite(night) && !group.nights.includes(night)) group.nights.push(night);
  });
  return Array.from(groups.values()).map((group) => {
    group.nights.sort((a, b) => a - b);
    group.nightLabel = nightLabel(group.nights);
    return group;
  });
}

// A stay visit carries the stay's pin, to tell apart two stays booked for the same night.
function samePoint(hotel, visit) {
  const lat = toNumber(visit.latitude);
  const lng = toNumber(visit.longitude);
  return hotel.hasPoint && lat != null && lng != null && Math.abs(hotel.lat - lat) < 1e-6 && Math.abs(hotel.lng - lng) < 1e-6;
}

export function nightLabel(nights) {
  const package_ = nights.filter((night) => night > 0);
  const pre = nights.filter((night) => night < 0);
  const parts = [];
  if (pre.length) parts.push(pre.length === 1 ? "Pre-night" : `${pre.length} pre-nights`);
  if (package_.length === 1) parts.push(`Night ${package_[0]}`);
  if (package_.length > 1) parts.push(`Nights ${package_.slice(0, -1).join(", ")} & ${package_[package_.length - 1]}`);
  return parts.join(" · ") || "Stay";
}

// Pings carry the day the driver app was on; when that does not match the plan, the date decides.
function trailDayIndex(point, dayByKey, detail, dayCount) {
  if (!dayCount) return 0;
  const byKey = point.dayKey ? dayByKey.get(point.dayKey) : null;
  if (byKey) return byKey.index;
  const start = parseDateOnly(detail.travelDate);
  const date = parseDateOnly(point.capturedAtIst);
  if (!start || !date) return 0;
  const diff = Math.floor((date.getTime() - start.getTime()) / 86400000);
  return Math.min(Math.max(diff, 0), dayCount - 1);
}

function currentDay(detail, dayCount, now) {
  if (!dayCount) return -1;
  if (detail.tripEnded) return dayCount - 1;
  const start = parseDateOnly(detail.travelDate);
  if (!start) return 0;
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diff = Math.floor((today.getTime() - start.getTime()) / 86400000);
  return Math.min(Math.max(diff, 0), dayCount - 1);
}

// The server sends IST wall-clock times without a zone, read the same way as the rest of the page.
export function parseTime(value) {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : null;
}

export function eventTime(event) {
  return parseTime(event?.capturedAtIst) || 0;
}

export function formatClock(value) {
  const time = parseTime(value);
  if (!time) return "-";
  return new Date(time).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "2 Nov, 8:05 AM": a clock time on another day than the one it is shown beside. */
export function formatDayClock(value) {
  const time = parseTime(value);
  if (!time) return "-";
  return `${new Date(time).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}, ${formatClock(value)}`;
}

/** "Since 5:08 PM" today, "Since yesterday 5:08 PM", or with the date further back. */
export function sinceLabel(value, now = new Date()) {
  const time = parseTime(value);
  if (!time) return "";
  const at = new Date(time);
  const days = Math.round(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()) - new Date(at.getFullYear(), at.getMonth(), at.getDate())) / 86400000,
  );
  if (days <= 0) return `Since ${formatClock(value)}`;
  if (days === 1) return `Since yesterday ${formatClock(value)}`;
  return `Since ${formatDayClock(value)}`;
}

export function sameDate(a, b) {
  const first = parseTime(a);
  const second = parseTime(b);
  return Boolean(first && second) && new Date(first).toDateString() === new Date(second).toDateString();
}

export function formatMinutes(totalMinutes) {
  if (totalMinutes == null || !Number.isFinite(totalMinutes) || totalMinutes < 0) return "-";
  const hours = Math.floor(totalMinutes / 60);
  const minutes = Math.round(totalMinutes % 60);
  if (hours && minutes) return `${hours} hr ${minutes} min`;
  if (hours) return `${hours} hr`;
  return `${minutes} min`;
}

export function minutesBetween(from, to) {
  const start = parseTime(from);
  const end = parseTime(to);
  if (!start || !end || end < start) return null;
  return Math.round((end - start) / 60000);
}

export function ageLabel(minutesAgo) {
  if (minutesAgo == null) return "";
  if (minutesAgo < 1) return "just now";
  if (minutesAgo < 60) return `${minutesAgo} min ago`;
  const hours = Math.floor(minutesAgo / 60);
  if (hours < 24) return `${hours} hr ago`;
  return `${Math.floor(hours / 24)} day${hours >= 48 ? "s" : ""} ago`;
}

// "Fri, 23 Oct" for a trip day, counted from the package's Day 1.
export function dayDate(travelDate, index) {
  const start = parseDateOnly(travelDate);
  if (!start) return "";
  const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
  return date.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

function parseDateOnly(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function toNumber(value) {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}
